/** ISO country options for club create/update (code stored in tbl_club.country). */
export const CLUB_COUNTRIES = [
  { code: "AD", name: "Andorra" },
  { code: "AL", name: "Albania" },
  { code: "AT", name: "Austria" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BE", name: "Belgium" },
  { code: "BG", name: "Bulgaria" },
  { code: "BY", name: "Belarus" },
  { code: "CH", name: "Switzerland" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DE", name: "Germany" },
  { code: "DK", name: "Denmark" },
  { code: "EE", name: "Estonia" },
  { code: "ES", name: "Spain" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GB", name: "United Kingdom" },
  { code: "GR", name: "Greece" },
  { code: "HR", name: "Croatia" },
  { code: "HU", name: "Hungary" },
  { code: "IE", name: "Ireland" },
  { code: "IS", name: "Iceland" },
  { code: "IT", name: "Italy" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "LV", name: "Latvia" },
  { code: "MC", name: "Monaco" },
  { code: "MD", name: "Moldova" },
  { code: "ME", name: "Montenegro" },
  { code: "MK", name: "North Macedonia" },
  { code: "MT", name: "Malta" },
  { code: "NL", name: "Netherlands" },
  { code: "NO", name: "Norway" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "RO", name: "Romania" },
  { code: "RS", name: "Serbia" },
  { code: "RU", name: "Russia" },
  { code: "SE", name: "Sweden" },
  { code: "SI", name: "Slovenia" },
  { code: "SK", name: "Slovakia" },
  { code: "SM", name: "San Marino" },
  { code: "UA", name: "Ukraine" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "BR", name: "Brazil" },
  { code: "AR", name: "Argentina" },
  { code: "AU", name: "Australia" },
  { code: "CN", name: "China" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "IN", name: "India" },
  { code: "MX", name: "Mexico" },
  { code: "ZA", name: "South Africa" },
];

const NAME_TO_CODE = Object.fromEntries(
  CLUB_COUNTRIES.map((c) => [c.name.toLowerCase(), c.code])
);

/** Normalize free-text / code to ISO alpha-2 when possible. */
export function normalizeClubCountry(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const byName = NAME_TO_CODE[raw.toLowerCase()];
  return byName || raw;
}

export function clubCountryLabel(codeOrName) {
  if (!codeOrName) return "";
  const normalized = normalizeClubCountry(codeOrName);
  const found = CLUB_COUNTRIES.find(
    (c) => c.code === normalized || c.name.toLowerCase() === String(codeOrName).toLowerCase()
  );
  return found ? `${found.name} (${found.code})` : String(codeOrName);
}

/** FLTT sync applies only to Luxembourg clubs. */
export function isLuxembourgClubCountry(codeOrName) {
  const normalized = normalizeClubCountry(codeOrName);
  if (!normalized) return false;
  const upper = String(normalized).toUpperCase();
  return upper === "LU" || upper === "LUXEMBOURG" || upper === "LUXEMBURG";
}

/** Country LU, or a club id issued for the Luxembourg federation. */
export function isLuxembourgFederationClubId(country, clubId) {
  if (isLuxembourgClubCountry(country)) return true;
  return String(clubId || "").startsWith("club_lu_");
}

function roleIdsOf(user) {
  const ids = [
    ...(Array.isArray(user?.all_roles) ? user.all_roles : []),
    user?.role_id,
  ];
  return ids.map((id) => String(id || "").toLowerCase()).filter(Boolean);
}

function roleNamesOf(user) {
  return (Array.isArray(user?.all_role_names) ? user.all_role_names : [])
    .map((name) => String(name || "").toLowerCase())
    .filter(Boolean);
}

/**
 * "Update from FLTT" — Luxembourg club Admins and Moderators (own club),
 * and Super Admin viewing a Luxembourg club. Coaches and non-LU clubs never see it.
 */
export function canUpdateClubFromFltt(user, clubCountry, clubId) {
  if (!user) return false;
  const userClubId = user.club_id != null ? String(user.club_id) : "";
  const targetId = clubId != null ? String(clubId) : "";
  const country =
    clubCountry || user.club?.country || user.club_details?.country || "";
  const clubToCheck = targetId || userClubId;
  if (!isLuxembourgFederationClubId(country, clubToCheck)) return false;

  const names = roleNamesOf(user);
  const roles = roleIdsOf(user);
  const isSuperAdmin =
    user.isSuperAdmin === true ||
    Number(user.type_user_id) === 1 ||
    names.includes("super admin") ||
    roles.includes("r1");
  if (isSuperAdmin) return true;

  const isClubModerator =
    user.hasModeratorRole === true ||
    names.includes("moderator") ||
    roles.includes("r9");
  const isClubAdmin =
    isClubModerator ||
    Number(user.type_user_id) === 2 ||
    user.hasAdminRole === true ||
    names.includes("admin") ||
    names.includes("club admin") ||
    roles.includes("r2");
  if (!isClubAdmin) return false;

  if (!targetId) return Boolean(userClubId);
  return Boolean(userClubId && userClubId === targetId);
}
